import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Edit,
  Visibility,
  Block,
  CheckCircle,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { superAdminAPI } from "../../services/api";
import { useSnackbar } from "notistack";

// Indian states for compliance
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const validationSchema = Yup.object({
  // Required fields
  name: Yup.string()
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name must be less than 100 characters")
    .required("Company name is required"),
  domain: Yup.string()
    .matches(
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/,
      "Invalid domain format"
    )
    .required("Domain is required"),
  adminEmail: Yup.string()
    .email("Invalid email format")
    .required("Admin email is required"),
  adminPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain uppercase, lowercase, number and special character"
    )
    .required("Admin password is required"),
  adminFirstName: Yup.string()
    .max(50, "First name must be less than 50 characters")
    .required("Admin first name is required"),
  adminLastName: Yup.string()
    .max(50, "Last name must be less than 50 characters")
    .required("Admin last name is required"),

  // Optional fields
  subscriptionPlan: Yup.string().oneOf([
    "starter",
    "professional",
    "enterprise",
  ]),
  subscriptionStatus: Yup.string().oneOf([
    "active",
    "suspended",
    "cancelled",
    "expired",
  ]),
  contactPhone: Yup.string().matches(
    /^\+91[6-9]\d{9}$/,
    "Invalid Indian phone number format"
  ),
  addressStreet: Yup.string(),
  addressCity: Yup.string(),
  addressState: Yup.string(),
  addressPincode: Yup.string().matches(
    /^[0-9]{6}$/,
    "Pincode must be exactly 6 digits"
  ),
  addressCountry: Yup.string(),
  gstNumber: Yup.string().matches(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    "Invalid GST number format"
  ),
  panNumber: Yup.string().matches(
    /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    "Invalid PAN number format"
  ),
});

const TenantManagement = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [viewDialog, setViewDialog] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    cancelled: 0,
    expired: 0,
  });

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getTenants({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        status: filterStatus !== "all" ? filterStatus : undefined,
        plan: filterPlan !== "all" ? filterPlan : undefined,
      });

      if (response.data.success) {
        const data = response.data.data;
        const tenantsData = data.tenants || [];
        const pagination = data.pagination || {};
        const summaryData = data.summary || {};

        setTenants(tenantsData);
        setTotalItems(pagination.totalItems || 0);
        setSummary(summaryData);
      } else {
        setTenants([]);
        setTotalItems(0);
        enqueueSnackbar("Failed to load tenants", { variant: "error" });
      }
    } catch (error) {
      console.error("Error loading tenants:", error);
      enqueueSnackbar("Failed to load tenants", { variant: "error" });
      setTenants([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    enqueueSnackbar,
    page,
    rowsPerPage,
    searchTerm,
    filterStatus,
    filterPlan,
  ]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleCreateTenant = async (values) => {
    try {
      if (editingTenant) {
        // Update existing tenant
        const updateData = {
          name: values.name,
          subscription: {
            plan: values.subscriptionPlan || "starter",
            status: values.subscriptionStatus || "active",
          },
          contactDetails: {
            primaryContact: {
              name: `${values.adminFirstName} ${values.adminLastName}`,
              email: values.adminEmail,
              phone: values.contactPhone || undefined,
            },
            address:
              values.addressStreet ||
              values.addressCity ||
              values.addressPincode
                ? {
                    street: values.addressStreet || undefined,
                    city: values.addressCity || undefined,
                    state: values.addressState || undefined,
                    pincode: values.addressPincode || undefined,
                    country: values.addressCountry || "India",
                  }
                : undefined,
          },
          compliance: {
            state: values.addressState || undefined,
            gstNumber: values.gstNumber || undefined,
            panNumber: values.panNumber || undefined,
          },
        };

        // Remove undefined values
        Object.keys(updateData).forEach((key) => {
          if (updateData[key] === undefined) {
            delete updateData[key];
          }
        });

        await superAdminAPI.updateTenant(editingTenant._id, updateData);
        enqueueSnackbar("Tenant updated successfully", { variant: "success" });
      } else {
        // Create new tenant
        const tenantData = {
          name: values.name,
          domain: values.domain,
          adminEmail: values.adminEmail,
          adminPassword: values.adminPassword,
          adminFirstName: values.adminFirstName,
          adminLastName: values.adminLastName,
          subscription: {
            plan: values.subscriptionPlan || "starter",
            status: values.subscriptionStatus || "active",
            expiresAt: new Date(
              Date.now() + 365 * 24 * 60 * 60 * 1000
            ).toISOString(), // 1 year from now
          },
          contactDetails: {
            primaryContact: {
              name: `${values.adminFirstName} ${values.adminLastName}`,
              email: values.adminEmail,
              phone: values.contactPhone || undefined,
            },
            address:
              values.addressStreet ||
              values.addressCity ||
              values.addressPincode
                ? {
                    street: values.addressStreet || undefined,
                    city: values.addressCity || undefined,
                    state: values.addressState || undefined,
                    pincode: values.addressPincode || undefined,
                    country: values.addressCountry || "India",
                  }
                : undefined,
          },
          compliance: {
            state: values.addressState || undefined,
            gstNumber: values.gstNumber || undefined,
            panNumber: values.panNumber || undefined,
          },
        };

        // Remove undefined values
        Object.keys(tenantData).forEach((key) => {
          if (tenantData[key] === undefined) {
            delete tenantData[key];
          }
        });

        await superAdminAPI.createTenant(tenantData);
        enqueueSnackbar("Tenant created successfully", { variant: "success" });
      }
      setOpenDialog(false);
      setEditingTenant(null);
      loadTenants();
    } catch (error) {
      console.error("Error creating tenant:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to save tenant";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    formik.setValues({
      name: tenant.name,
      domain: tenant.domain,
      adminEmail: tenant.contactDetails?.primaryContact?.email || "",
      adminPassword: "",
      adminFirstName:
        tenant.contactDetails?.primaryContact?.name?.split(" ")[0] || "",
      adminLastName:
        tenant.contactDetails?.primaryContact?.name
          ?.split(" ")
          .slice(1)
          .join(" ") || "",
      subscriptionPlan: tenant.subscription?.plan || "starter",
      subscriptionStatus: tenant.subscription?.status || "active",
      contactPhone: tenant.contactDetails?.primaryContact?.phone || "",
      addressStreet: tenant.contactDetails?.address?.street || "",
      addressCity: tenant.contactDetails?.address?.city || "",
      addressState:
        tenant.contactDetails?.address?.state || tenant.compliance?.state || "",
      addressPincode: tenant.contactDetails?.address?.pincode || "",
      addressCountry: tenant.contactDetails?.address?.country || "India",
      gstNumber: tenant.compliance?.gstNumber || "",
      panNumber: tenant.compliance?.panNumber || "",
    });
    setOpenDialog(true);
  };

  const handleSuspendTenant = async (tenantId, reason) => {
    try {
      await superAdminAPI.suspendTenant(tenantId, reason);
      enqueueSnackbar("Tenant suspended successfully", { variant: "success" });
      loadTenants();
    } catch (error) {
      console.error("Error suspending tenant:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to suspend tenant";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleReactivateTenant = async (tenantId) => {
    try {
      await superAdminAPI.reactivateTenant(tenantId);
      enqueueSnackbar("Tenant reactivated successfully", {
        variant: "success",
      });
      loadTenants();
    } catch (error) {
      console.error("Error reactivating tenant:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to reactivate tenant";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleDeleteTenant = async (tenantId, hardDelete = false) => {
    try {
      await superAdminAPI.deleteTenant(tenantId, hardDelete);
      enqueueSnackbar(
        hardDelete
          ? "Tenant permanently deleted"
          : "Tenant deactivated successfully",
        { variant: "success" }
      );
      loadTenants();
    } catch (error) {
      console.error("Error deleting tenant:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to delete tenant";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setViewDialog(true);
  };

  const formik = useFormik({
    initialValues: {
      // Required fields
      name: "",
      domain: "",
      adminEmail: "",
      adminPassword: "",
      adminFirstName: "",
      adminLastName: "",

      // Optional fields
      subscriptionPlan: "starter",
      subscriptionStatus: "active",
      contactPhone: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressPincode: "",
      addressCountry: "India",
      gstNumber: "",
      panNumber: "",
    },
    validationSchema,
    onSubmit: handleCreateTenant,
  });

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: "success", label: "Active" },
      suspended: { color: "warning", label: "Suspended" },
      cancelled: { color: "error", label: "Cancelled" },
      expired: { color: "default", label: "Expired" },
      inactive: { color: "default", label: "Inactive" },
    };
    const config = statusConfig[status] || statusConfig.inactive;

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getPlanChip = (plan) => {
    const planConfig = {
      starter: { color: "default", label: "Starter" },
      professional: { color: "primary", label: "Professional" },
      enterprise: { color: "secondary", label: "Enterprise" },
    };
    const config = planConfig[plan] || planConfig.starter;

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleMenuOpen = (event, tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTenant(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Tenant Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingTenant(null);
            formik.resetForm();
            setOpenDialog(true);
          }}
        >
          Add Tenant
        </Button>
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {summary.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Tenants
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="success.main">
                {summary.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="warning.main">
                {summary.suspended}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Suspended
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="error.main">
                {summary.cancelled}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cancelled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="text.secondary">
                {summary.expired}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expired
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={filterPlan}
                  onChange={(e) => setFilterPlan(e.target.value)}
                  label="Plan"
                >
                  <MenuItem value="all">All Plans</MenuItem>
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={loadTenants}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                    setFilterPlan("all");
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Statistics</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant._id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                        {tenant.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {tenant.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tenant.domain}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getPlanChip(tenant.subscription?.plan)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(
                      tenant.subscription?.status || tenant.isActive
                        ? "active"
                        : "inactive"
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tenant.contactDetails?.primaryContact?.name || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tenant.contactDetails?.primaryContact?.email || "N/A"}
                      </Typography>
                      {tenant.contactDetails?.primaryContact?.phone && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {tenant.contactDetails.primaryContact.phone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        Users: {tenant.stats?.users || 0}
                      </Typography>
                      <Typography variant="body2">
                        Workers: {tenant.stats?.workers || 0}
                      </Typography>
                      <Typography variant="body2">
                        Contractors: {tenant.stats?.contractors || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(tenant.createdAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        handleMenuOpen(e, tenant);
                      }}
                      size="small"
                      sx={{
                        visibility: "visible",
                        opacity: 1,
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          color: "text.primary",
                        },
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Add/Edit Tenant Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTenant ? "Edit Tenant" : "Add New Tenant"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Required Fields */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Required Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name *"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Domain *"
                  name="domain"
                  value={formik.values.domain}
                  onChange={formik.handleChange}
                  error={formik.touched.domain && Boolean(formik.errors.domain)}
                  helperText={formik.touched.domain && formik.errors.domain}
                  placeholder="company.clmssaas.com"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admin Email *"
                  name="adminEmail"
                  type="email"
                  value={formik.values.adminEmail}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.adminEmail &&
                    Boolean(formik.errors.adminEmail)
                  }
                  helperText={
                    formik.touched.adminEmail && formik.errors.adminEmail
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admin Password *"
                  name="adminPassword"
                  type="password"
                  value={formik.values.adminPassword}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.adminPassword &&
                    Boolean(formik.errors.adminPassword)
                  }
                  helperText={
                    formik.touched.adminPassword && formik.errors.adminPassword
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Status</InputLabel>
                  <Select
                    name="subscriptionStatus"
                    value={formik.values.subscriptionStatus}
                    onChange={formik.handleChange}
                    label="Subscription Status"
                    error={
                      formik.touched.subscriptionStatus &&
                      Boolean(formik.errors.subscriptionStatus)
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Plan</InputLabel>
                  <Select
                    name="subscriptionPlan"
                    value={formik.values.subscriptionPlan}
                    onChange={formik.handleChange}
                    label="Subscription Plan"
                  >
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Optional Fields */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Admin Details (Optional)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admin First Name"
                  name="adminFirstName"
                  value={formik.values.adminFirstName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.adminFirstName &&
                    Boolean(formik.errors.adminFirstName)
                  }
                  helperText={
                    formik.touched.adminFirstName &&
                    formik.errors.adminFirstName
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admin Last Name"
                  name="adminLastName"
                  value={formik.values.adminLastName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.adminLastName &&
                    Boolean(formik.errors.adminLastName)
                  }
                  helperText={
                    formik.touched.adminLastName && formik.errors.adminLastName
                  }
                />
              </Grid>

              {/* Contact Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Contact Details (Optional)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  name="contactPhone"
                  value={formik.values.contactPhone}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.contactPhone &&
                    Boolean(formik.errors.contactPhone)
                  }
                  helperText={
                    formik.touched.contactPhone && formik.errors.contactPhone
                  }
                  placeholder="+919876543210"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="addressStreet"
                  value={formik.values.addressStreet}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.addressStreet &&
                    Boolean(formik.errors.addressStreet)
                  }
                  helperText={
                    formik.touched.addressStreet && formik.errors.addressStreet
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="addressCity"
                  value={formik.values.addressCity}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.addressCity &&
                    Boolean(formik.errors.addressCity)
                  }
                  helperText={
                    formik.touched.addressCity && formik.errors.addressCity
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="addressPincode"
                  value={formik.values.addressPincode}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.addressPincode &&
                    Boolean(formik.errors.addressPincode)
                  }
                  helperText={
                    formik.touched.addressPincode &&
                    formik.errors.addressPincode
                  }
                  placeholder="400001"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="addressCountry"
                  value={formik.values.addressCountry}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.addressCountry &&
                    Boolean(formik.errors.addressCountry)
                  }
                  helperText={
                    formik.touched.addressCountry &&
                    formik.errors.addressCountry
                  }
                />
              </Grid>

              {/* Compliance Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Compliance Details (Optional)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="addressState"
                    value={formik.values.addressState}
                    onChange={formik.handleChange}
                    label="State"
                    error={
                      formik.touched.addressState &&
                      Boolean(formik.errors.addressState)
                    }
                  >
                    {INDIAN_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  name="gstNumber"
                  value={formik.values.gstNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.gstNumber && Boolean(formik.errors.gstNumber)
                  }
                  helperText={
                    formik.touched.gstNumber && formik.errors.gstNumber
                  }
                  placeholder="27ABCDE1234F1Z5"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PAN Number"
                  name="panNumber"
                  value={formik.values.panNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.panNumber && Boolean(formik.errors.panNumber)
                  }
                  helperText={
                    formik.touched.panNumber && formik.errors.panNumber
                  }
                  placeholder="ABCDE1234F"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTenant ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleEditTenant(selectedTenant);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleViewTenant(selectedTenant);
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedTenant?.subscription?.status === "active" ? (
          <MenuItem
            onClick={() => {
              handleSuspendTenant(selectedTenant._id, "Admin action");
              handleMenuClose();
            }}
          >
            <Block sx={{ mr: 1 }} />
            Suspend
          </MenuItem>
        ) : selectedTenant?.subscription?.status === "suspended" ? (
          <MenuItem
            onClick={() => {
              handleReactivateTenant(selectedTenant._id);
              handleMenuClose();
            }}
          >
            <CheckCircle sx={{ mr: 1 }} />
            Reactivate
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (
              window.confirm("Are you sure you want to deactivate this tenant?")
            ) {
              handleDeleteTenant(selectedTenant._id, false);
              handleMenuClose();
            }
          }}
          sx={{ color: "warning.main" }}
        >
          <Block sx={{ mr: 1 }} />
          Deactivate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (
              window.confirm(
                "Are you sure you want to permanently delete this tenant? This action cannot be undone!"
              )
            ) {
              handleDeleteTenant(selectedTenant._id, true);
              handleMenuClose();
            }
          }}
          sx={{ color: "error.main" }}
        >
          <Block sx={{ mr: 1 }} />
          Delete Permanently
        </MenuItem>
      </Menu>

      {/* Tenant Details View Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tenant Details</DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{selectedTenant.name}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Domain
                </Typography>
                <Typography variant="body1">{selectedTenant.domain}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                {getStatusChip(selectedTenant.status || "active")}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedTenant.createdAt).toLocaleDateString()}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Subscription Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Plan
                </Typography>
                {getPlanChip(selectedTenant.subscription?.plan)}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Max Users
                </Typography>
                <Typography variant="body1">
                  {selectedTenant.subscription?.maxUsers || 0}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Max Workers
                </Typography>
                <Typography variant="body1">
                  {selectedTenant.subscription?.maxWorkers || 0}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Max Contractors
                </Typography>
                <Typography variant="body1">
                  {selectedTenant.subscription?.maxContractors || 0}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Usage Statistics
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Users
                </Typography>
                <Typography variant="body1">
                  {selectedTenant.usage?.currentUsers || 0}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Workers
                </Typography>
                <Typography variant="body1">
                  {selectedTenant.usage?.currentWorkers || 0}
                </Typography>
              </Grid>

              {selectedTenant.contactDetails && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Contact Information
                    </Typography>
                  </Grid>

                  {selectedTenant.contactDetails.primaryContact?.phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {selectedTenant.contactDetails.primaryContact.phone}
                      </Typography>
                    </Grid>
                  )}

                  {selectedTenant.contactDetails.address && (
                    <>
                      {selectedTenant.contactDetails.address.street && (
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Address
                          </Typography>
                          <Typography variant="body1">
                            {selectedTenant.contactDetails.address.street}
                            {selectedTenant.contactDetails.address.city &&
                              `, ${selectedTenant.contactDetails.address.city}`}
                            {selectedTenant.contactDetails.address.pincode &&
                              ` - ${selectedTenant.contactDetails.address.pincode}`}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              handleEditTenant(selectedTenant);
              setViewDialog(false);
            }}
          >
            Edit Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Loading tenants...
            </Typography>
            <LinearProgress />
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default TenantManagement;
